import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import AuthService from "../../../api/services/AuthService";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import WalletTransferModals from "../WalletPage/WalletTransferModals";
import { useWalletTransfer } from "../WalletPage/useWalletTransfer";
import WalletOverviewSkeleton from "../../../customComponents/WalletOverviewSkeleton";
import "../FundingAccountPage/fundingAccount.css";
import "../AssetOverview/assetOverview.css";
import "../margin/margin.css";
import "../TradePage/trade_new.css";
import MarginLevelIndicator from "../margin/MarginLevelIndicator";
import IsolatedMarginRiskModal from "../margin/IsolatedMarginRiskModal";
import { pairHasDebt, parseMarginLevel, resolveMarginThresholds, buildMarginAccountLookup, normalizeMarginPairKey, enrichMarginAccounts, mergeMarginAccountSnapshots } from "../margin/marginLevelUtils";

/** Round to up to `decimals` significant decimal places, strip trailing zeros. */
function fmt(val, decimals = 8) {
  const n = parseFloat(val);
  if (!val || isNaN(n) || n === 0) return "0.00";
  return parseFloat(n.toFixed(decimals)).toString();
}

function fmtPrice(val) {
  const n = parseFloat(val);
  if (!val || isNaN(n) || n === 0) return "";
  return parseFloat(n.toFixed(2)).toString();
}

/** Merge wallet-balances rows (per coin) + accounts metadata (per pair) into one row per pair.
 *  Accounts data is used as primary fallback when wallet-balances rows are absent. */
function buildPairRows(balanceRows, accounts) {
  const accountLookup = buildMarginAccountLookup(accounts);

  // Seed from wallet-balance rows first
  const pairMap = {};
  for (const row of balanceRows) {
    if (!pairMap[row.pair]) pairMap[row.pair] = { pair: row.pair, base: null, quote: null };
    if (row.asset_type === "base") pairMap[row.pair].base = row;
    else pairMap[row.pair].quote = row;
  }

  // Ensure every account appears in the table even if wallet-balances returned nothing
  for (const acc of accounts) {
    const pairKey = acc.pair || normalizeMarginPairKey(null, acc.base_asset, acc.quote_asset);
    if (!pairMap[pairKey]) pairMap[pairKey] = { pair: pairKey, base: null, quote: null };
  }

  return Object.values(pairMap).map(({ pair, base, quote }) => {
    const acc = accountLookup.get(normalizeMarginPairKey(pair, base?.coin, quote?.coin))
      ?? accountLookup.get(String(pair).toUpperCase())
      ?? {};
    const ml = acc.margin_level != null && acc.margin_level !== "" ? parseFloat(acc.margin_level) : null;
    const mlDisplay = ml === null ? "—" : ml >= 999 ? "∞" : ml.toFixed(2);
    const borrowedBaseN = parseFloat(base?.borrowed ?? acc.base_borrowed ?? 0);
    const borrowedQuoteN = parseFloat(quote?.borrowed ?? acc.quote_borrowed ?? 0);
    const hasDebt = pairHasDebt({ ...acc, borrowedBase: borrowedBaseN, borrowedQuote: borrowedQuoteN }, ml);
    const leverage = parseMarginLevel(acc.leverage ?? acc.default_leverage ?? acc.max_leverage);
    const effectiveMultiple = parseMarginLevel(acc.effective_multiple ?? acc.effective_leverage ?? acc.leverage ?? acc.default_leverage);
    const { marginCallLevel, liquidationMarginLevel } = resolveMarginThresholds(acc);
    return {
      pair_id: acc.pair_id || "",
      pair: `${base?.coin || acc.base_asset || ""}/${quote?.coin || acc.quote_asset || ""}`,
      pairRaw: pair,
      base: base?.coin || acc.base_asset || "",
      quote: quote?.coin || acc.quote_asset || "",
      icon_path: acc.icon_path || "",
      marginLevel: ml,
      marginLevelDisplay: mlDisplay,
      hasDebt,
      leverage,
      effectiveMultiple,
      marginCallLevel,
      liquidationMarginLevel,
      status: acc.status || "NOT_OPENED",
      marginTier: acc.status === "NORMAL" ? "Tier 1" : null,
      availableBase: fmt(base?.available ?? acc.base_balance ?? "0"),
      availableQuote: fmt(quote?.available ?? acc.quote_balance ?? "0"),
      borrowableBase: fmt(base?.borrowable ?? acc.base_borrowable ?? "0"),
      borrowableQuote: fmt(quote?.borrowable ?? acc.quote_borrowable ?? "0"),
      loanCapBase: fmt(base?.loan_cap ?? acc.base_loan_cap ?? "0"),
      loanCapQuote: fmt(quote?.loan_cap ?? acc.quote_loan_cap ?? "0"),
      borrowedBase: fmt(base?.borrowed ?? acc.base_borrowed ?? "0"),
      borrowedQuote: fmt(quote?.borrowed ?? acc.quote_borrowed ?? "0"),
      frozenBase: fmt(base?.frozen ?? acc.base_locked ?? "0"),
      frozenQuote: fmt(quote?.frozen ?? acc.quote_locked ?? "0"),
      netBase: fmt(base
        ? (parseFloat(base.available || 0) + parseFloat(base.frozen || 0) - parseFloat(base.borrowed || 0)).toString()
        : (parseFloat(acc.base_balance || 0) + parseFloat(acc.base_locked || 0) - parseFloat(acc.base_borrowed || 0)).toString()),
      netQuote: fmt(quote
        ? (parseFloat(quote.available || 0) + parseFloat(quote.frozen || 0) - parseFloat(quote.borrowed || 0)).toString()
        : (parseFloat(acc.quote_balance || 0) + parseFloat(acc.quote_locked || 0) - parseFloat(acc.quote_borrowed || 0)).toString()),
      liqPrice: fmtPrice(base?.est_liquidation_price ?? quote?.est_liquidation_price ?? ""),
      markPrice: base?.mark_price ?? quote?.mark_price ?? null,
      netAssetsQuote: fmt(base?.net_assets_quote ?? quote?.net_assets_quote ?? "0"),
    };
  });
}

const COIN_RATES = {
  BNB: { hourly: "0.00034929%", annual: "3.05977500%" },
  USDT: { hourly: "0.00038596%", annual: "3.38099500%" },
  BTC: { hourly: "0.00004663%", annual: "0.40843500%" },
  ETH: { hourly: "0.00008219%", annual: "0.71998440%" },
  "0G": { hourly: "0.00050000%", annual: "4.38000000%" },
  "1INCH": { hourly: "0.00037917%", annual: "3.32150000%" },
  "2Z": { hourly: "0.00062500%", annual: "5.47500000%" },
};
const MODAL_ID = "marginBorrowRepayModal";

function dismissBootstrapModal(modalId) {
  const $el = window.$(`#${modalId}`);
  if ($el.length) $el.modal("hide");
  window.setTimeout(() => {
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  }, 300);
}

export function BorrowRepayModal({ row, initialTab, allPairs = [], onClose, onSuccess, onTransferClick, onCrossClick, onIsolatedClick, lockedMarginType, onOpenMarginRisk }) {
  const [tab, setTab] = useState(initialTab);
  const [marginType, setMarginType] = useState(lockedMarginType || "cross");
  const [selectedPair, setSelectedPair] = useState(row || allPairs[0]);
  const [pairDropOpen, setPairDropOpen] = useState(false);
  const [pairSearch, setPairSearch] = useState("");
  const [coinDropOpen, setCoinDropOpen] = useState(false);
  const [coinSearch, setCoinSearch] = useState("");
  const [coin, setCoin] = useState(row?.base || "");
  const [amount, setAmount] = useState("");
  const [hideZero, setHideZero] = useState(false);
  const [busy, setBusy] = useState(false);
  const [liveData, setLiveData] = useState(null);

  const [crossData, setCrossData] = useState(null);
  const [loadingCross, setLoadingCross] = useState(false);
  const [crossBorrowable, setCrossBorrowable] = useState(null);
  const [repayFull, setRepayFull] = useState(false);
  const [crossDebts, setCrossDebts] = useState([]);

  const fetchLiveData = useCallback((pairId) => {
    if (!pairId) return;
    AuthService.marginAccount(pairId)
      .then((res) => { if (res?.success) setLiveData(res.data); })
      .catch(() => { });
  }, []);

  const isCrossMode = lockedMarginType === "cross" || marginType === "cross";

  useEffect(() => {
    if (isCrossMode) return;
    setLiveData(null);
    fetchLiveData(selectedPair?.pair_id);
  }, [selectedPair?.pair_id, fetchLiveData, isCrossMode]);

  const refreshCrossAccount = useCallback(async () => {
    try {
      const res = await AuthService.crossAccount();
      if (res?.success) setCrossData(res.data);
    } catch {
      /* silent */
    }
  }, []);

  const refreshCrossDebts = useCallback(async () => {
    try {
      const res = await AuthService.crossDebts();
      if (res?.success) setCrossDebts(res.data?.debts || []);
      else setCrossDebts([]);
    } catch {
      setCrossDebts([]);
    }
  }, []);

  const refreshCrossBorrowable = useCallback(async (currencyId) => {
    if (!currencyId) {
      setCrossBorrowable(null);
      return;
    }
    try {
      const res = await AuthService.crossBorrowable(currencyId);
      if (res?.success) setCrossBorrowable(res.data);
      else setCrossBorrowable(null);
    } catch {
      setCrossBorrowable(null);
    }
  }, []);

  useEffect(() => {
    setLoadingCross(true);
    AuthService.crossAccount()
      .then((res) => {
        if (res?.success) setCrossData(res.data);
      })
      .catch(() => { })
      .finally(() => setLoadingCross(false));

    AuthService.crossDebts()
      .then((res) => { if (res?.success) setCrossDebts(res.data?.debts || []); })
      .catch(() => { });
  }, []);

  // Sync selectedPair when parent refreshes allPairs after borrow/repay/transfer
  useEffect(() => {
    if (!selectedPair?.pair_id) return;
    const updated = allPairs.find((p) => p.pair_id === selectedPair.pair_id);
    if (updated) setSelectedPair(updated);
  }, [allPairs]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBorrow = tab === "borrow";
  const isCoinBase = coin === selectedPair?.base;

  // Cross margin stats
  const crossAssetObj = crossData?.assets?.find((a) => a.asset === coin);
  /** Cross repay UI: always trust live GET /debts — never stale row.debt from modal open. */
  const crossDebtObj = isCrossMode
    ? crossDebts.find((d) => d.asset === coin) || null
    : (row?.base === coin ? row?.debt : null);

  // Resolve a valid MongoDB ObjectId for cross margin API calls.
  // The assets API may return the coin symbol instead of a real ObjectId, so
  // we validate (24 hex chars) and fall through to the debts response which
  // consistently carries the real currency ObjectId.
  const isObjectId = (v) => typeof v === "string" && /^[a-f\d]{24}$/i.test(v);
  const crossCurrencyId =
    (isObjectId(row?.currency_id) ? row.currency_id : null) ||
    (isObjectId(crossDebtObj?.currency_id) ? crossDebtObj.currency_id : null) ||
    (isObjectId(crossAssetObj?.currency_id) ? crossAssetObj.currency_id : null) ||
    null;

  // Borrow tab → GET /v1/cross/borrowable only
  useEffect(() => {
    if (!isCrossMode || !isBorrow) {
      setCrossBorrowable(null);
      return;
    }
    refreshCrossBorrowable(crossCurrencyId);
  }, [isCrossMode, isBorrow, crossCurrencyId, coin, refreshCrossBorrowable]);

  // Repay tab → GET /v1/cross/debts + /v1/cross/account (live interest preview)
  useEffect(() => {
    if (!isCrossMode || isBorrow) return;
    refreshCrossDebts();
    refreshCrossAccount();
    const poll = setInterval(() => {
      refreshCrossDebts();
      refreshCrossAccount();
    }, 5000);
    return () => clearInterval(poll);
  }, [isCrossMode, isBorrow, coin, refreshCrossDebts, refreshCrossAccount]);
  const crossMaxBorrow = crossBorrowable?.borrowable ? parseFloat(crossBorrowable.borrowable).toFixed(8).replace(/\.?0+$/, "") : "0";
  const crossFreeBalance = isCrossMode
    ? (crossAssetObj?.balance ?? "0")
    : (crossAssetObj?.balance || row?.freeBalance || "0");
  const crossOutstandingTotal = parseFloat(
    crossDebtObj?.outstanding ?? ((parseFloat(crossDebtObj?.principal || 0) + parseFloat(crossDebtObj?.interest_accrued || 0)).toFixed(8))
  );
  const crossMaxRepay = Math.min(parseFloat(crossFreeBalance || 0), crossOutstandingTotal).toFixed(8).replace(/\.?0+$/, "");


  const uniqueCoinsList = useMemo(() => {
    const coinSet = new Set();
    const isCross = marginType === "cross" || lockedMarginType === "cross";
    if (isCross) {
      (crossData?.assets || []).forEach((a) => { if (a.asset) coinSet.add(a.asset); });
      crossDebts.forEach((d) => { if (d.asset) coinSet.add(d.asset); });
      if (row?.base) coinSet.add(row.base);
    } else {
      allPairs.forEach((p) => {
        if (p.base) coinSet.add(p.base);
        if (p.quote) coinSet.add(p.quote);
      });
      if (allPairs.length === 0) {
        if (row?.base) coinSet.add(row.base);
        if (row?.quote) coinSet.add(row.quote);
      }
    }
    return Array.from(coinSet).sort().map((c) => ({ coin: c }));
  }, [allPairs, row, marginType, lockedMarginType, crossData, crossDebts]);

  // Prefer live API data; fall back to the pre-built row values
  const borrowable = isCoinBase
    ? (liveData?.borrowable?.base ?? selectedPair?.borrowableBase)
    : (liveData?.borrowable?.quote ?? selectedPair?.borrowableQuote);
  const borrowed = isCoinBase
    ? (liveData?.balances?.base_borrowed ?? selectedPair?.borrowedBase)
    : (liveData?.balances?.quote_borrowed ?? selectedPair?.borrowedQuote);
  const available = isCoinBase
    ? (liveData?.balances?.base_available ?? selectedPair?.availableBase)
    : (liveData?.balances?.quote_available ?? selectedPair?.availableQuote);

  const ml = liveData?.margin_level != null ? parseFloat(liveData.margin_level) : null;
  const liqPrice = fmtPrice(liveData?.est_liq_price ?? selectedPair?.liqPrice ?? "");
  const hourlyRate = liveData?.interest?.hourly_pct ?? COIN_RATES[coin]?.hourly ?? "—";
  const annualRate = liveData?.interest?.annualized_pct ?? COIN_RATES[coin]?.annual ?? "—";

  useEffect(() => {
    if (row?.base) setCoin(row.base);
  }, [row?.base]);

  useEffect(() => {
    setTab(initialTab);
    setAmount("");
    setRepayFull(false);
  }, [initialTab, row?.base]);

  const switchTab = (t) => { setTab(t); setAmount(""); setRepayFull(false); };
  const switchPair = (p) => { setSelectedPair(p); setCoin(p.base); setAmount(""); setPairDropOpen(false); };

  const handleConfirm = async () => {
    if (isCrossMode) {
      if (!crossCurrencyId) { alertErrorMessage("Currency ID not available. Please try again."); return; }
      setBusy(true);
      try {
        let result;
        if (isBorrow) {
          if (!amount || parseFloat(amount) <= 0) { alertErrorMessage("Enter a valid amount"); setBusy(false); return; }
          if (parseFloat(amount) > parseFloat(crossMaxBorrow)) { alertErrorMessage("Amount exceeds borrowable limit"); setBusy(false); return; }
          result = await AuthService.crossBorrow({ currency_id: crossCurrencyId, amount });
        } else {
          if (crossOutstandingTotal <= 0) { alertErrorMessage("No outstanding loan for this asset"); setBusy(false); return; }
          if (!repayFull && (!amount || parseFloat(amount) <= 0)) { alertErrorMessage("Enter a valid amount"); setBusy(false); return; }
          if (!repayFull && parseFloat(amount) > parseFloat(crossMaxRepay)) { alertErrorMessage("Amount exceeds repayable limit"); setBusy(false); return; }
          result = await AuthService.crossRepay({ currency_id: crossCurrencyId, amount: repayFull ? undefined : amount });
        }
        if (result?.success) {
          if (!isBorrow && result.data?.fully_repaid) {
            setCrossDebts((prev) => prev.filter((d) => d.asset !== coin));
          }
          await Promise.all([refreshCrossAccount(), refreshCrossDebts()]);
          if (isBorrow) await refreshCrossBorrowable(crossCurrencyId);
          alertSuccessMessage(result.message || `${isBorrow ? "Borrowed" : "Repaid"} successfully`);
          setAmount("");
          setRepayFull(false);
          if (onSuccess) await onSuccess();
        } else {
          alertErrorMessage(result?.message || "Operation failed");
        }
      } catch (err) {
        alertErrorMessage(err?.message || "Operation failed");
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alertErrorMessage(`Please enter a valid ${isBorrow ? "loan" : "repayment"} amount`);
      return;
    }
    if (isBorrow && parseFloat(amount) > parseFloat(borrowable)) {
      alertErrorMessage("Amount exceeds borrowable limit"); return;
    }
    if (!isBorrow && parseFloat(amount) > parseFloat(borrowed)) {
      alertErrorMessage("Amount exceeds outstanding loan"); return;
    }
    setBusy(true);
    try {
      const assetType = isCoinBase ? "base" : "quote";
      let result;
      if (isBorrow) {
        result = await AuthService.marginBorrow(selectedPair.pair_id, assetType, amount);
      } else {
        result = await AuthService.marginRepay({ pairId: selectedPair.pair_id, assetType, amount: String(amount) });
      }
      if (result?.success) {
        alertSuccessMessage(result.message || `${isBorrow ? "Borrowed" : "Repaid"} ${amount} ${coin} successfully`);
        setAmount("");
        fetchLiveData(selectedPair.pair_id);
        if (onSuccess) onSuccess();
      } else {
        alertErrorMessage(result?.message || "Operation failed");
      }
    } catch (err) {
      alertErrorMessage(err?.message || "Operation failed");
    } finally {
      setBusy(false);
    }
  };

  const renderCrossAssetPicker = () => (
    <>
      <label className="repay-label">Asset</label>
      <div className="repay-input-container">
        <div className="repay-input-wrapper">
          <div onClick={() => setCoinDropOpen(!coinDropOpen)} className="coin-dropdown-trigger">
            {coin && (
              <div className="coin-icon-small">
                <span className="coin-icon-text">{coin.substring(0, 2)}</span>
              </div>
            )}
            <span className="coin-symbol">{coin || ""}</span>
            <i className={coinDropOpen ? "ri-arrow-up-s-fill coin-dropdown-icon" : "ri-arrow-down-s-fill coin-dropdown-icon"} />
          </div>
        </div>
        {coinDropOpen && (
          <div className="isolated-pair-popover shadow-sm">
            <div className="px-3 py-2 border-bottom search-header">
              <div className="search-input-wrapper">
                <i className="ri-search-line search-icon" />
                <input
                  type="text" placeholder="Search" value={coinSearch} onChange={(e) => setCoinSearch(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            <div className="popover-list">
              {uniqueCoinsList
                .filter((c) => c.coin.toLowerCase().includes(coinSearch.toLowerCase()))
                .map((c) => (
                  <div
                    key={c.coin}
                    className={`isolated-pair-option ${coin === c.coin ? "active" : ""}`}
                    onClick={() => { setCoin(c.coin); setCoinDropOpen(false); setCoinSearch(""); setAmount(""); setRepayFull(false); }}
                  >
                    <div className="coin-info-group">
                      <div className="coin-icon-medium">
                        <span className="coin-icon-text-medium">{c.coin.substring(0, 2)}</span>
                      </div>
                      <div className="coin-text-group">
                        <span className="coin-name">{c.coin}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </>
  );

  let hasAssets = true;
  if (isCrossMode) {
    if (!loadingCross && crossData) {
      const assets = crossData.assets || [];
      hasAssets = assets.some(a => parseFloat(a.balance || 0) > 0 || parseFloat(a.borrowed || 0) > 0 || parseFloat(a.locked || 0) > 0);
    } else if (!loadingCross) {
      hasAssets = false; // no data means no assets
    }
  } else {
    const isoHasBase = parseFloat(selectedPair?.availableBase || 0) > 0 || parseFloat(selectedPair?.frozenBase || 0) > 0 || parseFloat(selectedPair?.borrowedBase || 0) > 0;
    const isoHasQuote = parseFloat(selectedPair?.availableQuote || 0) > 0 || parseFloat(selectedPair?.frozenQuote || 0) > 0 || parseFloat(selectedPair?.borrowedQuote || 0) > 0;
    hasAssets = isoHasBase || isoHasQuote;
  }

  const handleTransferClick = () => {
    onClose();
    if (onTransferClick) {
      onTransferClick(marginType === "isolated" ? selectedPair : null, marginType);
    }
  };

  return (
    <div className="modal fade kyc_modal margin-modal-container" id={MODAL_ID} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0 pt-4 px-4 d-flex align-items-center justify-content-between">
            <div className="margin-modal-tabs" >
              <div
                className={`margin-modal-tab-wrapper ${isBorrow ? "active" : ""}`}
                onClick={() => switchTab("borrow")}

              >
                Manual Borrow
                {isBorrow && <div className="margin-modal-tab-indicator"></div>}
              </div>
              <div
                className={`margin-modal-tab-wrapper ${!isBorrow ? "active" : ""}`}
                onClick={() => switchTab("repay")}

              >
                Manual Repay
                {!isBorrow && <div className="margin-modal-tab-indicator"></div>}
              </div>
            </div>
            <button type="button" className="btn-close m-0 margin-modal-close" aria-label="Close" onClick={onClose} />
          </div>

          <div className="modal-body pt-1 px-4 pb-4">
            {!lockedMarginType && (
              <div className="margin-type-toggle-container" >
                <div
                  onClick={onCrossClick || (() => setMarginType("cross"))}
                  className={`margin-type-toggle ${marginType === "cross" ? "active" : ""}`}
                >
                  Cross Margin
                </div>
                <div
                  onClick={onIsolatedClick || (() => setMarginType("isolated"))}
                  className={`margin-type-toggle ${marginType === "isolated" ? "active" : ""}`}
                >
                  Isolated Margin
                </div>
              </div>
            )}

            {isCrossMode ? (
              isBorrow ? (
                !hasAssets && !loadingCross ? (
                  <div className="margin-modal-empty-state">
                    <div className="margin-modal-empty-icon">
                      <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="66" cy="48" r="26" stroke="#1E2329" strokeWidth="2.5" fill="#FFFFFF" />
                        <circle cx="66" cy="48" r="18" stroke="#1E2329" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
                        <rect x="60" y="42" width="12" height="12" rx="1" transform="rotate(45 66 48)" stroke="#1E2329" strokeWidth="2.5" fill="none" />
                        <circle cx="54" cy="64" r="26" stroke="#1E2329" strokeWidth="2.5" fill="#FFFFFF" />
                        <circle cx="54" cy="64" r="18" stroke="#1E2329" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
                        <rect x="48" y="58" width="12" height="12" rx="1" transform="rotate(45 54 64)" stroke="#1E2329" strokeWidth="2.5" fill="none" />
                        <circle cx="74" cy="74" r="12" fill="#1E2329" />
                        <path d="M69 69L79 79M79 69L69 79" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </div>
                    <p className="margin-modal-empty-text">Your cross margin account currently has no assets, please make a transfer transaction first.</p>
                    <button type="button" className="btn margin-modal-transfer-btn" onClick={handleTransferClick}>
                      Transfer
                    </button>
                  </div>
                ) : (
                  <div className="wallet_transfer mt-4">
                    {renderCrossAssetPicker()}
                    {crossBorrowable && (
                      <div className="margin-info-rows mb-2">
                        <div className="margin-info-row">
                          <span>Borrowable</span>
                          <span>{crossMaxBorrow} {coin}</span>
                        </div>
                        {crossBorrowable.binding && (
                          <div className="margin-info-row">
                            <span>Limited by</span>
                            <span style={{ color: "#8E949E", fontSize: 12 }}>
                              {crossBorrowable.binding === "equity" ? "Your margin capacity"
                                : crossBorrowable.binding === "pool" ? "Lending pool liquidity"
                                  : crossBorrowable.binding === "user_cap" ? "Per-user limit"
                                    : crossBorrowable.binding}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="crypto_selectcoin crypto_selectcoin-2">
                      <h6>Loan Amount</h6>
                      <div className="price_max_total">
                        <input type="number" placeholder="Enter amount to borrow" value={amount}
                          onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.target.blur()} />
                        <div className="d-flex gap-0">
                          {coin}
                          <button type="button" onClick={() => setAmount(crossMaxBorrow)}>Max</button>
                        </div>
                      </div>
                    </div>
                    <div className="margin-modal-note">
                      <i className="ri-information-fill" />
                      <span>Borrowed funds are available immediately. Interest accrues against your shared collateral.</span>
                    </div>
                    <div className="margin-modal-actions" style={{ marginTop: 16 }}>
                      <button type="button" className="btn margin-modal-btn-cancel" disabled={busy} onClick={onClose}>Cancel</button>
                      <button type="button" className="btn margin-modal-btn-confirm" disabled={busy} onClick={handleConfirm}>
                        {busy ? "Processing…" : "Confirm Borrow"}
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div className="wallet_transfer mt-4">
                  {renderCrossAssetPicker()}

                  {crossOutstandingTotal > 0 ? (
                    <>
                      <div className="margin-info-rows mt-3 mb-2">
                        <div className="margin-info-row">
                          <span>Principal</span>
                          <span>{fmt(crossDebtObj?.principal)} {coin}</span>
                        </div>
                        <div className="margin-info-row">
                          <span>Accrued Interest</span>
                          <span style={{ color: parseFloat(crossDebtObj?.interest_accrued) > 0 ? "#e45561" : undefined }}>
                            {fmt(crossDebtObj?.interest_accrued)} {coin}
                          </span>
                        </div>
                        <div className="margin-info-row" style={{ borderTop: "1px solid rgba(128,128,128,0.15)", paddingTop: 4, marginTop: 2 }}>
                          <span>Total Owed</span>
                          <span style={{ fontWeight: 600 }}>{fmt(crossOutstandingTotal)} {coin}</span>
                        </div>
                        <div className="margin-info-row">
                          <span>Free Balance</span>
                          <span>{fmt(crossFreeBalance)} {coin}</span>
                        </div>
                      </div>
                      <label className="d-flex align-items-center gap-2 mb-3" style={{ cursor: "pointer", fontSize: 13 }}>
                        <input type="checkbox" checked={repayFull} onChange={(e) => { setRepayFull(e.target.checked); setAmount(""); }}
                          style={{ width: 14, height: 14 }} />
                        Repay All (clears full debt including accrued interest)
                      </label>
                      {!repayFull && (
                        <div className="crypto_selectcoin crypto_selectcoin-2">
                          <h6>Repayment Amount</h6>
                          <div className="price_max_total">
                            <input type="number" placeholder="Enter amount to repay" value={amount}
                              onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.target.blur()} />
                            <div className="d-flex gap-0">
                              {coin}
                              <button type="button" onClick={() => setAmount(crossMaxRepay)}>Max</button>
                            </div>
                          </div>
                          <legend className="margin-modal-legend" style={{ marginTop: 4, fontSize: "12px" }}>
                            Max repayable: <span>{fmt(crossMaxRepay)} {coin}</span>
                          </legend>
                        </div>
                      )}
                      <div className="margin-modal-note">
                        <i className="ri-information-fill" />
                        <span>Interest is settled first, then principal. Partial repayment is allowed at any time.</span>
                      </div>
                      <div className="margin-modal-actions" style={{ marginTop: 16 }}>
                        <button type="button" className="btn margin-modal-btn-cancel" disabled={busy} onClick={onClose}>Cancel</button>
                        <button type="button" className="btn margin-modal-btn-confirm" disabled={busy} onClick={handleConfirm}>
                          {busy ? "Processing…" : "Confirm Repay"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="margin-modal-empty-note mt-3">
                      <i className="ri-error-warning-line empty-note-icon" />
                      <span>You haven't borrowed any {coin} yet.</span>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="isolated-margin-container modern-margin-form mt-4">
                {/* Pair Selector */}
                <div className="isolated-pair-selector-wrap repay-input-container">
                  <label className="repay-label">
                    Isolated Margin Pair
                  </label>
                  <div
                    className="isolated-pair-input"
                    onClick={() => setPairDropOpen(!pairDropOpen)}
                  >
                    <span className="coin-symbol">{selectedPair?.pair || ""}</span>
                    <i className={pairDropOpen ? "ri-arrow-up-s-fill" : "ri-arrow-down-s-fill"} />
                  </div>

                  {pairDropOpen && (
                    <div className="isolated-pair-popover shadow-sm">
                      <div className="px-3 py-2 border-bottom search-header">
                        <div className="search-input-wrapper">
                          <i className="ri-search-line search-icon" />
                          <input
                            type="text"
                            placeholder="search options"
                            value={pairSearch}
                            onChange={(e) => setPairSearch(e.target.value)}
                            className="search-input"
                          />
                        </div>
                      </div>
                      <div className="popover-list">
                        {allPairs
                          .filter(p => p?.pair?.toLowerCase()?.includes(pairSearch?.toLowerCase() || ""))
                          .map((p) => (
                            <div
                              key={p.pair}
                              className={`isolated-pair-option${selectedPair?.pair === p.pair ? " active" : ""}`}
                              onClick={() => { switchPair(p); setPairDropOpen(false); setPairSearch(""); }}
                            >
                              <span className="coin-symbol">{p.pair}</span>
                              <div className="coin-balance-group">
                                <div className="coin-balance-val">{parseFloat(p.availableBase || 0) > 0 ? parseFloat(p.availableBase).toFixed(6) : "0"}</div>
                                <div>$0.00</div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {isBorrow ? (
                  !hasAssets ? (
                    <div className="margin-modal-empty-note mt-4 mt-3">
                      <i className="ri-error-warning-line empty-note-icon" />
                      <span>Your isolated margin account currently has no assets, please make a transfer transaction first.</span>
                    </div>
                  ) : (
                    <div className="wallet_transfer mt-4">
                      <div className="margin-modal-empty-note mt-3 mb-3">
                        <i className="ri-error-warning-line empty-note-icon" />
                        <span>Your assets may be automatically liquidated. Only borrow what you can afford to repay.</span>
                      </div>
                      <label className="repay-label">Coin</label>
                      <div className="margin-coin-radios mt-0 margin-modal-actions mt-2 mb-3">
                        {[selectedPair?.base, selectedPair?.quote].filter(Boolean).map((c) => (
                          <div
                            key={c}
                            className={`isolated-coin-card ${coin === c ? "active" : ""}`}
                            onClick={() => { setCoin(c); setAmount(""); }}
                          >
                            <div className="coin-card-header">
                              <div className="coin-icon-small">
                                <span className="coin-icon-text">{c.substring(0, 2)}</span>
                              </div>
                              <span style={{ fontWeight: 600, color: '#1E2329', fontSize: '14px' }}>{c}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="crypto_selectcoin crypto_selectcoin-2">
                        <h6>Loan Amount</h6>
                        <div className="price_max_total">
                          <input type="number" placeholder="Enter amount to borrow" value={amount}
                            onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.target.blur()} />
                          <div className="d-flex gap-0">
                            {coin}
                            <button type="button" onClick={() => setAmount(String(borrowable || ""))}>Max</button>
                          </div>
                        </div>
                      </div>
                      <div className="margin-info-rows mb-2 mt-2">
                        <div className="margin-info-row"><span>Maximum Borrow Amount</span><span>{fmt(borrowable)} {coin}</span></div>
                        {liqPrice && <div className="margin-info-row"><span>Est. Liq Price</span><span>{liqPrice} {selectedPair?.quote}</span></div>}
                        <div className="margin-info-row"><span>Hourly Interest Rate</span><span>{hourlyRate}</span></div>
                        <div className="margin-info-row"><span>Annualized Interest Rate</span><span>{annualRate}</span></div>
                        <div className="margin-info-row"><span>Borrowed</span><span>{fmt(borrowed)} {coin}</span></div>
                        <div className="margin-info-row">
                          <span>Borrow Margin Level</span>
                          <span>
                            <MarginLevelIndicator
                              ml={ml}
                              hasDebt={pairHasDebt({
                                balances: liveData?.balances,
                                base_borrowed: selectedPair?.borrowedBase,
                                quote_borrowed: selectedPair?.borrowedQuote,
                              })}
                              thresholds={resolveMarginThresholds(liveData || selectedPair)}
                              onInfoClick={() => onOpenMarginRisk?.({
                                pair_id: selectedPair?.pair_id,
                                pair: selectedPair?.pair,
                                leverage: liveData?.leverage ?? liveData?.default_leverage ?? selectedPair?.leverage,
                                margin_level: ml,
                                margin_call_level: liveData?.margin_call_level ?? selectedPair?.marginCallLevel,
                                liquidation_margin_level: liveData?.liquidation_margin_level ?? selectedPair?.liquidationMarginLevel,
                                base_borrowed: liveData?.balances?.base_borrowed ?? selectedPair?.borrowedBase,
                                quote_borrowed: liveData?.balances?.quote_borrowed ?? selectedPair?.borrowedQuote,
                              })}
                              showTooltip={false}
                            />
                          </span>
                        </div>
                        {selectedPair?.marginTier && (
                          <div className="margin-info-row"><span>Current Margin Tier</span><span>{selectedPair.marginTier}</span></div>
                        )}
                      </div>
                      <div className="margin-modal-note">
                        <i className="ri-information-fill" />
                        <span>Borrowed funds are subject to hourly interest charges starting immediately. If your margin level falls below the maintenance threshold, your position may be automatically liquidated. Only borrow what you can afford to repay.</span>
                      </div>
                      <div className="margin-modal-actions" style={{ marginTop: 16 }}>
                        <button type="button" className="btn margin-modal-btn-cancel" disabled={busy} onClick={onClose}>Cancel</button>
                        <button type="button" className="btn margin-modal-btn-confirm" disabled={busy} onClick={handleConfirm}>
                          {busy ? "Processing…" : "Confirm Borrow"}
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="wallet_transfer mt-4">
                    <label className="repay-label">Coin</label>
                    <div className="margin-coin-radios mt-0 margin-modal-actions mt-4">
                      {[selectedPair?.base, selectedPair?.quote].filter(Boolean).map((c) => {
                        const isBase = c === selectedPair?.base;
                        const bor = isBase
                          ? (liveData?.balances?.base_borrowed ?? selectedPair?.borrowedBase)
                          : (liveData?.balances?.quote_borrowed ?? selectedPair?.borrowedQuote);
                        return (
                          <div
                            key={c}
                            className={`isolated-coin-card ${coin === c ? "active" : ""}`}
                            onClick={() => { setCoin(c); setAmount(""); setRepayFull(false); }}
                          >
                            <div className="coin-card-header">
                              <div className="coin-icon-small">
                                <span className="coin-icon-text">{c.substring(0, 2)}</span>
                              </div>
                              <span style={{ fontWeight: 600, color: '#1E2329', fontSize: '14px' }}>{c}</span>
                            </div>
                            <div className="coin-card-body">
                              <span style={{ fontWeight: 500, color: '#1E2329', fontSize: '13px', lineHeight: '1.2' }}>{fmt(bor)}</span>
                              <span style={{ color: '#707A8A', fontSize: '12px', marginTop: '4px' }}>Borrowed</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {parseFloat(borrowed || 0) > 0 ? (
                      <>
                        <div className="margin-info-rows mb-2 mt-3">
                          <div className="margin-info-row"><span>Outstanding Loan</span><span>{fmt(borrowed)} {coin}</span></div>
                          <div className="margin-info-row"><span>Available Balance</span><span>{fmt(available)} {coin}</span></div>
                        </div>
                        <label className="d-flex align-items-center gap-2 mb-3" style={{ cursor: "pointer", fontSize: 13 }}>
                          <input type="checkbox" checked={repayFull}
                            onChange={(e) => { setRepayFull(e.target.checked); if (e.target.checked) setAmount(String(borrowed || "")); else setAmount(""); }}
                            style={{ width: 14, height: 14 }} />
                          Repay All
                        </label>
                        {!repayFull && (
                          <div className="crypto_selectcoin crypto_selectcoin-2">
                            <h6>Repayment Amount</h6>
                            <div className="price_max_total">
                              <input type="number" placeholder="Enter amount to repay" value={amount}
                                onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.target.blur()} />
                              <div className="d-flex gap-0">
                                {coin}
                                <button type="button" onClick={() => setAmount(String(borrowed || ""))}>Max</button>
                              </div>
                            </div>
                            <legend className="margin-modal-legend">Available: <span>{fmt(available)} {coin}</span></legend>
                          </div>
                        )}
                        <div className="margin-modal-note">
                          <i className="ri-information-fill" />
                          <span>Interest is settled first from your repayment amount, with the remainder applied to the principal. You may repay partially or in full at any time.</span>
                        </div>
                        <div className="margin-modal-actions" style={{ marginTop: 16 }}>
                          <button type="button" className="btn margin-modal-btn-cancel" disabled={busy} onClick={onClose}>Cancel</button>
                          <button type="button" className="btn margin-modal-btn-confirm" disabled={busy} onClick={handleConfirm}>
                            {busy ? "Processing…" : "Confirm Repay"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="margin-modal-empty-note mt-4 mt-3">
                        <i className="ri-error-warning-line empty-note-icon" />
                        <span>You haven't borrowed any {coin || 'assets'} yet.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MarginAccountPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showBalance, setShowBalance] = useState(true);
  const mask = (val) => (showBalance ? val : "********");
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = useRef(null);

  const searchContainerRef = useRef(null);

  const toggleSearch = useCallback(() => {
    setSearchActive((v) => !v);
  }, []);

  useEffect(() => {
    if (!searchActive) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 320);
    return () => window.clearTimeout(t);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchActive(false);
    };
    const onClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchActive(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [searchActive]);
  const [hideSmall, setHideSmall] = useState(false);
  const [liabilitiesOnly, setLiabilitiesOnly] = useState(false);
  const [modalState, setModalState] = useState(null);
  const [marginRiskModalRow, setMarginRiskModalRow] = useState(null);
  const [marginRowPopup, setMarginRowPopup] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [rawAccounts, setRawAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const rawAccountsRef = useRef([]);

  useEffect(() => {
    rawAccountsRef.current = rawAccounts;
  }, [rawAccounts]);

  const fetchData = useCallback(async () => {
    try {
      const [balRes, accRes, sumRes] = await Promise.all([
        AuthService.marginWalletBalances(),
        AuthService.marginAccounts(),
        AuthService.marginPortfolioSummary(),
      ]);
      if (accRes?.success || true) {
        let acc = accRes?.data || [];

        // --- ADDED DUMMY DATA FOR TESTING POPUP ---
        if (acc.length === 0) {
          acc = [
            {
              pair_id: "dummy_1",
              pair: "BTC/USDT",
              base_asset: "BTC",
              quote_asset: "USDT",
              status: "NORMAL",
              base_balance: "1.5",
              quote_balance: "15000.00",
              base_borrowable: "5.0",
              quote_borrowable: "50000.00",
              base_loan_cap: "10.0",
              quote_loan_cap: "100000.00",
              base_borrowed: "0.5",
              quote_borrowed: "5000.00",
              base_locked: "0.1",
              quote_locked: "1000.00",
              est_liquidation_price: "25000.00",
              margin_level: "1.5",
              margin_call_level: "1.19",
              liquidation_margin_level: "1.15",
              leverage: 5,
              effective_multiple: 5,
              default_leverage: 5,
            },
            {
              pair_id: "dummy_2",
              pair: "ETH/USDT",
              base_asset: "ETH",
              quote_asset: "USDT",
              status: "NORMAL",
              base_balance: "10.5",
              quote_balance: "25000.00",
              base_borrowable: "50.0",
              quote_borrowable: "150000.00",
              base_loan_cap: "100.0",
              quote_loan_cap: "300000.00",
              base_borrowed: "2.5",
              quote_borrowed: "15000.00",
              base_locked: "1.1",
              quote_locked: "2000.00",
              est_liquidation_price: "1500.00",
              margin_level: "2.5",
              margin_call_level: "1.25",
              liquidation_margin_level: "1.18",
              leverage: 3,
              effective_multiple: 3,
              default_leverage: 3,
            }
          ];
        }
        // ------------------------------------------
        const enrichedAcc = await enrichMarginAccounts(acc);
        // Use wallet-balances rows if the endpoint returns them; otherwise the
        // accounts endpoint already carries all balance fields and buildPairRows
        // falls back to acc.base_balance / acc.quote_balance automatically.
        const balRows = (balRes?.success && Array.isArray(balRes.data) && balRes.data.length > 0)
          ? balRes.data
          : [];
        setPairs(buildPairRows(balRows, enrichedAcc));
        setRawAccounts(enrichedAcc);
      }
      if (sumRes?.success) setSummary(sumRes.data);
    } catch {
      // silent — show empty state
    } finally {
      setInitialLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshMarginWalletData = useCallback(async () => {
    try {
      const [balRes, accRes] = await Promise.all([
        AuthService.marginWalletBalances(),
        AuthService.marginAccounts(),
      ]);
      if (!accRes?.data) return;
      const merged = mergeMarginAccountSnapshots(rawAccountsRef.current, accRes.data);
      rawAccountsRef.current = merged;
      setRawAccounts(merged);
      const balRows = (balRes?.success && Array.isArray(balRes.data) && balRes.data.length > 0)
        ? balRes.data
        : [];
      setPairs(buildPairRows(balRows, merged));
    } catch {
      /* keep existing rows */
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (initialLoading) return undefined;
    void refreshMarginWalletData();
    const id = window.setInterval(() => void refreshMarginWalletData(), 3000);
    return () => window.clearInterval(id);
  }, [initialLoading, refreshMarginWalletData]);

  const walletTransfer = useWalletTransfer({
    initialFrom: "spot",
    initialTo: "margin",
    onTransferSuccess: async () => { fetchData(); },
  });

  const openTransferModal = (row) => {
    if (row) {
      const pair = rawAccounts.find((a) => a.pair_id === row.pair_id) || rawAccounts[0] || null;
      if (pair) walletTransfer.setSelectedMarginPair(pair);
      walletTransfer.setMarginAssetType("base");
    }
    walletTransfer.openTransferModal({ from: "spot", to: "margin" });
    setTimeout(() => window.$("#walletTransferModal").modal("show"), 0);
  };

  const filtered = useMemo(() => {
    let rows = pairs;
    if (liabilitiesOnly) rows = rows.filter((r) => parseFloat(r.borrowedBase) > 0 || parseFloat(r.borrowedQuote) > 0);
    if (hideSmall) rows = rows.filter((r) => parseFloat(r.availableBase) >= 1 || parseFloat(r.availableQuote) >= 1);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.pair.toLowerCase().includes(q) || r.base.toLowerCase().includes(q));
    return rows;
  }, [pairs, search, hideSmall, liabilitiesOnly]);

  const openModal = (row, tab) => {
    setModalState({ row, tab });
    setTimeout(() => window.$(`#${MODAL_ID}`).modal("show"), 0);
  };

  const closeBorrowRepayModal = useCallback(() => {
    dismissBootstrapModal(MODAL_ID);
    setModalState(null);
  }, []);

  const openMarginRiskModal = useCallback((row) => {
    setMarginRiskModalRow(row);
  }, []);

  const closeMarginRiskModal = useCallback(() => {
    setMarginRiskModalRow(null);
  }, []);

  useEffect(() => {
    const $el = window.$(`#${MODAL_ID}`);
    const onHidden = () => {
      setModalState(null);
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    };
    $el.on("hidden.bs.modal", onHidden);
    return () => $el.off("hidden.bs.modal", onHidden);
  }, []);

  if (initialLoading) {
    return <WalletOverviewSkeleton variant="margin-account" />;
  }

  return (
    <div className="dashboard_right funding-account margin-pro-page">
      <div className="funding-account__header border-0">
        <div>
          <h1 className="funding-account__title">Isolated Margin Account</h1>
        </div>
        <div className="funding-account__header-actions">
          <button type="button" className="funding-account__pill" onClick={() => openTransferModal(null)}>
            Transfer
          </button>
        </div>
      </div>

      <div className="funding-account__summarycard">
        <div className="funding-account__summary-top d-flex align-items-center gap-2">
          <div className="funding-account__totals-label h4 m-0">Total Assets</div>
          <i
            className={showBalance ? "ri-eye-line" : "ri-eye-off-line"}
            style={{ cursor: "pointer", color: "#707A8A", fontSize: "16px" }}
            onClick={() => setShowBalance(!showBalance)}
          />
        </div>

        <div className="d-flex marginwallet_card">

          <div className="funding-account__summary-main">
            <div className="funding-account__summary-amount">
              <div className="funding-account__summary-num">{mask(summary?.total_assets_usd ?? "0.00")}</div>
              <div className="funding-account__summary-unit">USD</div>
            </div>
            <div className="funding-account__summary-sub">≈{mask(summary?.total_assets_btc ?? "0.00000000")} BTC</div>
            <div className="funding-account__summary-pnl">
              <span>Today's PnL</span>
              <strong>{mask((summary?.today_pnl_usd ?? "0.00") + " USD")}</strong>
            </div>
          </div>
          <div className="funding-account__summary-grid">
            <div className="funding-account__summary-stat">
              <div className="funding-account__summary-stat-k">Account Equity</div>
              <div className="funding-account__summary-stat-v">{mask(summary?.account_equity_usd ?? "0.00")} <span style={{ fontSize: 12 }}>USD</span></div>
            </div>
            <div className="funding-account__summary-stat funding-account__summary-stat--right">
              <div className="funding-account__summary-stat-k">Total Liabilities</div>
              <div className="funding-account__summary-stat-v">{mask(summary?.total_liabilities_usd ?? "0.00")} <span style={{ fontSize: 12 }}>USD</span></div>
            </div>
          </div>

        </div>

      </div>

      <div className="funding-account__card mt-3">
        <div className="funding-account__filters funding-account__filters--tight margin-acct__filters rightsearch">
          <div
            ref={searchContainerRef}
            className={`searchBar custom-tabs ${searchActive ? "search_active" : ""}`}
            onClick={toggleSearch}
          >
            <i className="ri-search-2-line" aria-hidden="true" />
            <input
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              type="search"
              className="custom_search"
              aria-label="Search"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <label className="funding-account__check">
            <input type="checkbox" checked={liabilitiesOnly} onChange={(e) => setLiabilitiesOnly(e.target.checked)} />
            Liabilities only
          </label>
          <label className="funding-account__check">
            <input type="checkbox" checked={hideSmall} onChange={(e) => setHideSmall(e.target.checked)} />
            Hide assets &lt; $1
          </label>
        </div>

        <div className="funding-account__tablewrap">
          <table className="funding-account__table funding-account__table--margin">
            <thead>
              <tr>
                <th>Pair</th>
                <th>Coin</th>
                <th className="ta-r">Available</th>
                <th className="ta-r">Borrowable</th>
                <th className="ta-r">Loan Cap</th>
                <th className="ta-r">Borrowed</th>
                <th className="ta-r">Frozen</th>
                <th className="ta-r">Net Assets</th>
                <th className="ta-r">Est. Liquidation Price</th>
                <th className="ta-r">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.pair}>
                  <td className="funding-account__pair" data-label="Pair">
                    <div className="funding-account__coin">
                      <img
                        className="funding-account__coin-img"
                        src={r.icon_path ? resolvePublicUploadUrl(r.icon_path) : "/images/default_coin.png"}
                        alt={r.pair}
                        onError={(e) => { e.target.src = "/images/AnnouncementImg/activities_icon.png"; }}
                      />
                      <span>{r.pair}</span>
                    </div>
                    {r.status === "NOT_OPENED"
                      ? <span style={{ fontSize: 10, color: "#8e949e", background: "rgba(142,148,158,0.1)", borderRadius: 3, padding: "1px 5px" }}>Not opened</span>
                      : (
                        <MarginLevelIndicator
                          variant="wallet"
                          ml={r.marginLevel}
                          hasDebt={r.hasDebt}
                          thresholds={{ marginCallLevel: r.marginCallLevel, liquidationMarginLevel: r.liquidationMarginLevel }}
                          onInfoClick={() => openMarginRiskModal(r)}
                          showTooltip={false}
                        />
                      )
                    }
                  </td>
                  <td data-label="Coin">
                    <div className="funding-account__coin-sym">{r.base}</div>
                    <div className="funding-account__coin-name">{r.quote}</div>
                  </td>
                  <td className="ta-r" data-label="Available">
                    <div className="funding-account__num">{mask(r.availableBase)}</div>
                    <div className="funding-account__subnum">{mask(r.availableQuote)}</div>
                  </td>
                  <td className="ta-r" data-label="Borrowable">
                    <div className="funding-account__num">{mask(r.borrowableBase)}</div>
                    <div className="funding-account__subnum">{mask(r.borrowableQuote)}</div>
                  </td>
                  <td className="ta-r" data-label="Loan Cap">
                    <div className="funding-account__num">{mask(r.loanCapBase)}</div>
                    <div className="funding-account__subnum">{mask(r.loanCapQuote)}</div>
                  </td>
                  <td className="ta-r" data-label="Borrowed">
                    <div className="funding-account__num">{mask(r.borrowedBase)}</div>
                    <div className="funding-account__subnum">{mask(r.borrowedQuote)}</div>
                  </td>
                  <td className="ta-r" data-label="Frozen">
                    <div className="funding-account__num">{mask(r.frozenBase)}</div>
                    <div className="funding-account__subnum">{mask(r.frozenQuote)}</div>
                  </td>
                  <td className="ta-r margin-acct__net-cell" data-label="Net Assets">
                    <div className="d-flex align-items-center justify-content-end gap-2">
                      <div>
                        <div className="funding-account__num">{mask(r.netBase)}</div>
                        <div className="funding-account__subnum">{mask(r.netQuote)}</div>
                      </div>
                      <button
                        type="button"
                        className="agce-ao-more-btn d-inline-flex d-lg-none flex-shrink-0"
                        aria-label="View full row"
                        onClick={() => setMarginRowPopup(r)}
                      >
                        <i className="ri-more-2-fill" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                  <td className="ta-r" data-label="Est. Liquidation Price">
                    <div className="funding-account__num">{r.liqPrice || "—"}</div>
                  </td>
                  <td className="ta-r" data-label="Action">
                    <div className="funding-account__actions">
                      <button type="button" className="funding-account__action funding-account__action--btn" onClick={() => openModal(r, "borrow")}>Borrow/Repay</button>
                      <button type="button" className="funding-account__action funding-account__action--btn" onClick={() => openTransferModal(r)}>Transfer</button>
                      <button type="button" className="funding-account__action funding-account__action--btn" onClick={() => navigate(`/margin/${r.pair.replace("/", "_")}`)}>Trade</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-4 text-muted">No assets found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile row detail popup */}
      {marginRowPopup && (
        <>
          <div className="agce-ao-detail-backdrop" role="presentation" onClick={() => setMarginRowPopup(null)} />
          <div
            className="modal fade show d-flex agce-ao-sheet-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="marginRowPopupTitle"
            style={{ zIndex: 1060 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable agce-ao-sheet-dialog">
              <div className="modal-content agce-ao-sheet border-0">
                <h2 id="marginRowPopupTitle" className="visually-hidden">
                  {marginRowPopup.pair} details
                </h2>
                <div className="agce-ao-sheet__header">
                  <div className="agce-ao-sheet__brand">
                    <img
                      className="agce-ao-sheet__coin"
                      src={marginRowPopup.icon_path ? resolvePublicUploadUrl(marginRowPopup.icon_path) : "/images/default_coin.png"}
                      alt=""
                      onError={(e) => { e.target.src = "/images/AnnouncementImg/activities_icon.png"; }}
                    />
                    <div>
                      <div className="agce-ao-sheet__sym">{marginRowPopup.pair}</div>
                      <div className="agce-ao-sheet__fullname">{marginRowPopup.base} / {marginRowPopup.quote}</div>
                    </div>
                  </div>
                  <button type="button" className="btn-close agce-ao-sheet__close" aria-label="Close" onClick={() => setMarginRowPopup(null)} />
                </div>
                <div className="modal-body agce-ao-sheet__body">
                  <div className="agce-ao-sheet__hero">
                    <div className="agce-ao-sheet__hero-num">{marginRowPopup.netBase} {marginRowPopup.base}</div>
                    <div className="agce-ao-sheet__hero-fiat">{marginRowPopup.netQuote} {marginRowPopup.quote}</div>
                  </div>
                  <div className="agce-ao-sheet__kvlist">
                    <div className="agce-ao-sheet__kv">
                      <span>Available</span>
                      <span className="agce-ao-sheet__kv-val text-end">
                        <span className="d-block">{marginRowPopup.availableBase} {marginRowPopup.base}</span>
                        <span className="d-block funding-account__subnum">{marginRowPopup.availableQuote} {marginRowPopup.quote}</span>
                      </span>
                    </div>
                    <div className="agce-ao-sheet__kv">
                      <span>Borrowable</span>
                      <span className="agce-ao-sheet__kv-val text-end">
                        <span className="d-block">{marginRowPopup.borrowableBase} {marginRowPopup.base}</span>
                        <span className="d-block funding-account__subnum">{marginRowPopup.borrowableQuote} {marginRowPopup.quote}</span>
                      </span>
                    </div>
                    <div className="agce-ao-sheet__kv">
                      <span>Loan Cap</span>
                      <span className="agce-ao-sheet__kv-val text-end">
                        <span className="d-block">{marginRowPopup.loanCapBase} {marginRowPopup.base}</span>
                        <span className="d-block funding-account__subnum">{marginRowPopup.loanCapQuote} {marginRowPopup.quote}</span>
                      </span>
                    </div>
                    <div className="agce-ao-sheet__kv">
                      <span>Borrowed</span>
                      <span className="agce-ao-sheet__kv-val text-end">
                        <span className="d-block">{marginRowPopup.borrowedBase} {marginRowPopup.base}</span>
                        <span className="d-block funding-account__subnum">{marginRowPopup.borrowedQuote} {marginRowPopup.quote}</span>
                      </span>
                    </div>
                    <div className="agce-ao-sheet__kv">
                      <span>Frozen</span>
                      <span className="agce-ao-sheet__kv-val text-end">
                        <span className="d-block">{marginRowPopup.frozenBase} {marginRowPopup.base}</span>
                        <span className="d-block funding-account__subnum">{marginRowPopup.frozenQuote} {marginRowPopup.quote}</span>
                      </span>
                    </div>
                    <div className="agce-ao-sheet__kv">
                      <span>Est. Liq. Price</span>
                      <span className="agce-ao-sheet__kv-val">{marginRowPopup.liqPrice || "—"}</span>
                    </div>
                    {marginRowPopup.status !== "NOT_OPENED" && (
                      <div className="agce-ao-sheet__kv">
                        <span>Margin Level</span>
                        <span className="agce-ao-sheet__kv-val">
                          <MarginLevelIndicator
                            variant="wallet"
                            ml={marginRowPopup.marginLevel}
                            hasDebt={marginRowPopup.hasDebt}
                            thresholds={{ marginCallLevel: marginRowPopup.marginCallLevel, liquidationMarginLevel: marginRowPopup.liquidationMarginLevel }}
                            onInfoClick={() => {
                              setMarginRowPopup(null);
                              openMarginRiskModal(marginRowPopup);
                            }}
                            showTooltip={false}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="agce-ao-sheet__divider" />
                  <div className="agce-ao-sheet__table-actions">
                    <button type="button" className="funding-account__action funding-account__action--btn" onClick={() => { setMarginRowPopup(null); openModal(marginRowPopup, "borrow"); }}>Borrow/Repay</button>
                    <button type="button" className="funding-account__action funding-account__action--btn" onClick={() => { setMarginRowPopup(null); openTransferModal(marginRowPopup); }}>Transfer</button>
                    <button type="button" className="funding-account__action funding-account__action--btn" onClick={() => { setMarginRowPopup(null); navigate(`/margin/${marginRowPopup.pair.replace("/", "_")}`); }}>Trade</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <BorrowRepayModal
        key={modalState ? `${modalState.row?.pair}-${modalState.tab}` : "empty"}
        row={modalState?.row}
        initialTab={modalState?.tab || "borrow"}
        allPairs={pairs}
        onClose={closeBorrowRepayModal}
        onSuccess={fetchData}
        onOpenMarginRisk={openMarginRiskModal}
        lockedMarginType="isolated"
        onTransferClick={(row, type) => {
          if (type === "cross") {
            walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" });
            setTimeout(() => window.$("#walletTransferModal").modal("show"), 0);
          } else {
            openTransferModal(row);
          }
        }}
      />

      {marginRiskModalRow && (
        <IsolatedMarginRiskModal row={marginRiskModalRow} onClose={closeMarginRiskModal} />
      )}

      <WalletTransferModals
        fromWalletType={walletTransfer.fromWalletType}
        setFromWalletType={walletTransfer.setFromWalletType}
        toWalletType={walletTransfer.toWalletType}
        setToWalletType={walletTransfer.setToWalletType}
        availableWalletTypes={walletTransfer.availableWalletTypes}
        walletLabelsMap={walletTransfer.walletLabelsMap}
        selectedCurrency={walletTransfer.selectedCurrency}
        setSelectedCurrency={walletTransfer.setSelectedCurrency}
        transferAmount={walletTransfer.transferAmount}
        setTransferAmount={walletTransfer.setTransferAmount}
        transferable={walletTransfer.transferable}
        balanceLoading={walletTransfer.balanceLoading}
        sourceHint={walletTransfer.sourceHint}
        handleInterchangeWallet={walletTransfer.handleInterchangeWallet}
        handleTransfer={walletTransfer.handleTransfer}
        currencyData={walletTransfer.currencyData}
        fiveDecimal={walletTransfer.fiveDecimal}
        marginInvolved={walletTransfer.marginInvolved}
        marginPairs={walletTransfer.marginPairs.length ? walletTransfer.marginPairs : rawAccounts}
        selectedMarginPair={walletTransfer.selectedMarginPair}
        setSelectedMarginPair={walletTransfer.setSelectedMarginPair}
        marginAssetType={walletTransfer.marginAssetType}
        setMarginAssetType={walletTransfer.setMarginAssetType}
        marginPairDropOpen={walletTransfer.marginPairDropOpen}
        setMarginPairDropOpen={walletTransfer.setMarginPairDropOpen}
      />
    </div>
  );
}
